import { useState } from "react";
import type { ComponentProps } from "./index";
import { Markdown } from "../markdown";
import { GONE_NOTICE, isGone, isGoneStatus, parseInteractiveResult, submitInteractiveResult } from "./interactive";
import { answeredPick, parseAnswerArgs, shapeAnswer, type AnswerResult } from "./answerCard.ts";

type SubmitState = { phase: "idle" } | { phase: "submitting"; pick: number | null } | { phase: "submitted"; pick: number | null } | { phase: "failed"; status: number; message: string };

/**
 * An answer card: one question and two to four options, formulas typeset in both, answered with one tap
 * so nothing with a formula has to be typed. A box under the options takes an answer of their own.
 * Like Form, its arguments are the model's own and ride along as data. Once answered it shows the pick.
 */
export function Answer({ sessionId, callId, args, result }: ComponentProps): React.ReactNode {
	const parsed = parseAnswerArgs(args);
	const [own, setOwn] = useState("");
	const [submit, setSubmit] = useState<SubmitState>({ phase: "idle" });

	if (!parsed.ok) return <div className="component error">{parsed.error}</div>;

	const answered = result ? parseInteractiveResult(result) : undefined;
	const gone = isGone(answered, submit.phase === "failed" ? submit.status : undefined);
	const locked = answered !== undefined || submit.phase === "submitting" || submit.phase === "submitted" || (submit.phase === "failed" && isGoneStatus(submit.status));
	const picked = answered?.kind === "value" ? answeredPick(answered.value) : submit.phase === "submitting" || submit.phase === "submitted" ? submit.pick : null;
	const ownAnswer = answered?.kind === "value" && typeof answered.value.own === "string" ? answered.value.own : null;

	async function send(answer: AnswerResult) {
		setSubmit({ phase: "submitting", pick: answer.choice });
		const outcome = await submitInteractiveResult(sessionId, callId, answer);
		setSubmit(outcome.ok ? { phase: "submitted", pick: answer.choice } : { phase: "failed", status: outcome.status, message: outcome.message });
	}

	return (
		<div className="component answer-card">
			<div className="answer-question">
				<Markdown text={parsed.question} />
			</div>
			<div className="answer-options">
				{parsed.options.map((option, i) => (
					<button
						key={i}
						type="button"
						className={`answer-option${picked === i ? " picked" : ""}`}
						disabled={locked}
						onClick={() => void send(shapeAnswer(parsed.options, { pick: i }))}
					>
						<span className="answer-letter">{"ABCD"[i]}</span>
						<Markdown text={option} />
					</button>
				))}
			</div>
			{parsed.allowOwn && !locked && (
				<div className="answer-own">
					<input value={own} placeholder="其他答案" onChange={(e) => setOwn(e.target.value)} />
					<button type="button" disabled={!own.trim()} onClick={() => void send(shapeAnswer(parsed.options, { own }))}>
						提交
					</button>
				</div>
			)}
			{ownAnswer && <p className="answer-own-given">其他答案：{ownAnswer}</p>}
			{gone && <p className="hint">{GONE_NOTICE.form}</p>}
			{answered === undefined && submit.phase === "failed" && !gone && <p className="error">{submit.message}</p>}
		</div>
	);
}
